/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/**
 * DR runs (DR Phase 10): what a run did step by step, and the operator's controls while it runs or after it
 * failed. Swift's workflow engine runs the steps; the portal only shows them and sends pause, resume, cancel,
 * retry or roll back.
 */

import * as React from "react";
import styled from "styled-components";
import { Ban, CheckCheck, Pause, Play, RotateCcw, Trash2, Undo2 } from "lucide-react";

import { RUN_OPERATION_NAMES, drErrorMessage } from "@src/components/modules/DrModule/drUtils";
import { ActionButton } from "@src/components/ui/DetailsKit";
import { Muted, Pill, T9, formatDateTime, timeAgo } from "@src/components/ui/Lists/ListTable";
import type { PillTone } from "@src/components/ui/Lists/ListTable";
import { Notice, OpsInput } from "@src/components/ui/OpsDialog";
import drSource from "@src/sources/DrSource";

import type { DrRun, DrRunAction, DrRunStep } from "@src/@types/Dr";

export const RUN_STATES: { [state: string]: { label: string; tone: PillTone } } = {
  PENDING: { label: "Pending", tone: "neutral" },
  VALIDATING: { label: "Validating", tone: "brand" },
  AWAITING_APPROVAL: { label: "Awaiting approval", tone: "warning" },
  RUNNING: { label: "Running", tone: "brand" },
  PAUSED: { label: "Paused", tone: "warning" },
  SUCCEEDED: { label: "Succeeded", tone: "success" },
  FAILED: { label: "Failed", tone: "danger" },
  RETRYING: { label: "Retrying", tone: "brand" },
  ROLLING_BACK: { label: "Rolling back", tone: "warning" },
  ROLLED_BACK: { label: "Rolled back", tone: "neutral" },
  CANCELLING: { label: "Cancelling", tone: "warning" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
};
const STEP_STATES: { [state: string]: { label: string; tone: PillTone } } = {
  PENDING: { label: "Waiting", tone: "neutral" },
  RUNNING: { label: "Running", tone: "brand" },
  SUCCEEDED: { label: "Done", tone: "success" },
  FAILED: { label: "Failed", tone: "danger" },
  SKIPPED: { label: "Skipped", tone: "neutral" },
  COMPENSATED: { label: "Undone", tone: "neutral" },
};
export const OPERATION_LABELS = RUN_OPERATION_NAMES;

/** What the operator can do to a run in this state (Swift checks again). A test failover that has ended can
 * have its test VMs cleaned up (a new clean-up run); a completed failback is confirmed by a new run that removes
 * the DR copies. */
export const actionsFor = (state: string, operation?: string): DrRunAction[] => {
  const cleanup: DrRunAction[] =
    operation === "TEST_FAILOVER" && ["SUCCEEDED", "FAILED", "CANCELLED", "ROLLED_BACK"].includes(state)
      ? ["cleanup"]
      : operation === "FAILBACK" && state === "SUCCEEDED"
        ? ["confirm"]
        : [];
  switch (state) {
    case "RUNNING":
      return ["pause", "cancel"];
    case "PAUSED":
      return ["resume", "cancel"];
    case "FAILED":
      return ["retry", "rollback", "cancel", ...cleanup];
    default:
      return cleanup;
  }
};
const ACTIONS: { [action in DrRunAction]: { label: string; icon: React.ElementType; variant?: "secondary" | "danger" } } = {
  pause: { label: "Pause", icon: Pause, variant: "secondary" },
  resume: { label: "Resume", icon: Play },
  retry: { label: "Retry failed steps", icon: RotateCcw },
  rollback: { label: "Roll back", icon: Undo2, variant: "danger" },
  cancel: { label: "Cancel run", icon: Ban, variant: "danger" },
  cleanup: { label: "Clean up test VMs", icon: Trash2, variant: "secondary" },
  confirm: { label: "Confirm failback…", icon: CheckCheck, variant: "secondary" },
};

const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;
const Step = styled.li`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  gap: 2px 10px;
  padding: 7px 0;
  border-bottom: 1px solid ${T9.slate100};
  font-size: 13px;
  line-height: 18px;
  color: ${T9.slate700};
  word-break: break-word;
`;
const Index = styled.span`
  color: ${T9.slate500};
  font-variant-numeric: tabular-nums;
`;
const Kind = styled.span`
  font-weight: 600;
  color: ${T9.slate900};
`;
const Detail = styled.div`
  grid-column: 2 / 4;
  font-size: 12px;
  color: ${T9.slate500};
`;
const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const stepLabel = (kind: string) => kind.replace(/[_.]/g, " ").replace(/^./, c => c.toUpperCase());

const stepDetail = (step: DrRunStep): string => {
  const parts: string[] = [];
  if (step.max_attempts > 1 || step.attempt > 1) parts.push(`attempt ${step.attempt} of ${step.max_attempts}`);
  if (step.state === "PENDING" && step.next_attempt_at) parts.push(`next try ${timeAgo(step.next_attempt_at)}`);
  if (step.depends_on.length) parts.push(`after step ${step.depends_on.map(d => d + 1).join(", ")}`);
  if (step.finished_at) parts.push(`finished ${timeAgo(step.finished_at)}`);
  else if (step.started_at) parts.push(`started ${timeAgo(step.started_at)}`);
  return parts.join(" · ");
};

type RunViewProps = {
  runId: string;
  /** Called after an action changed the run. */
  onChanged?: () => void;
};
export const RunView = (props: RunViewProps) => {
  const [run, setRun] = React.useState<DrRun | null>(null);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState<DrRunAction | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [followUp, setFollowUp] = React.useState<{ action: DrRunAction; runId: string } | null>(null);

  const load = React.useCallback(async () => {
    try {
      setRun(await drSource.run(props.runId));
    } catch (err) {
      setError(drErrorMessage(err, "Could not load the run."));
    }
  }, [props.runId]);
  React.useEffect(() => {
    load();
  }, [load]);
  // An active run changes on its own: look again every 10 seconds (the engine's pace).
  const active = run ? !["SUCCEEDED", "FAILED", "ROLLED_BACK", "CANCELLED"].includes(run.state) : false;
  React.useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [active, load]);

  const act = async (action: DrRunAction) => {
    setBusy(action);
    setError(null);
    try {
      const result = await drSource.runAction(props.runId, action, reason.trim() || undefined);
      if (action === "cleanup" || action === "confirm") setFollowUp({ action, runId: result.id });  // a new run; this one stays as it was
      else setRun(result);
      setReason("");
      props.onChanged?.();
    } catch (err) {
      setError(drErrorMessage(err, "SwiftIQ refused this."));
    } finally {
      setBusy(null);
    }
  };

  if (!run) return error ? <Notice tone="danger">{error}</Notice> : <Muted>Loading…</Muted>;
  const state = RUN_STATES[run.state] || { label: run.state, tone: "neutral" as PillTone };
  const actions = actionsFor(run.state, run.operation);
  return (
    <div data-test-id="dr-run-view" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {followUp?.action === "cleanup" ? (
        <Notice tone="success" title="Clean-up started">
          The test VMs are being removed by run {followUp.runId}; it appears in the plan's runs.
        </Notice>
      ) : followUp?.action === "confirm" ? (
        <Notice tone="success" title="Confirmation requested">
          Run {followUp.runId} removes the DR copies and what the failback kept once someone else approves it in Change
          control. It appears in the plan's runs.
        </Notice>
      ) : null}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <Pill tone={state.tone}>{state.label}</Pill>
        <Muted>
          {OPERATION_LABELS[run.operation] || run.operation}
          {run.plan_version ? ` of version ${run.plan_version}` : ""}
          {run.requested_by ? ` · by ${run.requested_by}` : ""}
          {" · "}
          <span title={formatDateTime(run.started_at || run.created_at)}>{timeAgo(run.started_at || run.created_at)}</span>
        </Muted>
      </div>
      {run.state_reason && run.state !== "SUCCEEDED" ? <Muted>{run.state_reason}</Muted> : null}
      {run.steps && run.steps.length ? (
        <Steps>
          {run.steps.map(step => {
            const s = STEP_STATES[step.state] || { label: step.state, tone: "neutral" as PillTone };
            const detail = stepDetail(step);
            return (
              <Step key={step.id} data-test-id={`dr-run-step-${step.index}`}>
                <Index>{step.index + 1}</Index>
                <Kind>{stepLabel(step.kind)}</Kind>
                <span>
                  <Pill tone={s.tone}>{s.label}</Pill>
                </span>
                {detail ? <Detail>{detail}</Detail> : null}
                {step.error ? <Detail style={{ color: "var(--t9-danger-700)" }}>{step.error}</Detail> : null}
              </Step>
            );
          })}
        </Steps>
      ) : (
        <Muted>This run has no steps.</Muted>
      )}
      {actions.length ? (
        <Controls>
          <OpsInput
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
            placeholder="Reason (recorded in the audit log)"
            style={{ flex: "1 1 200px" }}
            data-test-id="dr-run-reason"
          />
          {actions.map(action => {
            const a = ACTIONS[action];
            const Icon = a.icon;
            return (
              <ActionButton
                key={action}
                type="button"
                variant={a.variant}
                disabled={Boolean(busy)}
                onClick={() => act(action)}
                data-test-id={`dr-run-${action}`}
              >
                <Icon size={16} /> {a.label}
              </ActionButton>
            );
          })}
        </Controls>
      ) : null}
    </div>
  );
};
